import React, { useState, useEffect } from 'react';
import { X, Package, MapPin, CheckCircle, AlertCircle, Settings, Play, Loader2, Copy, Download, Eye, ChevronDown, ChevronUp, Info, PlusSquare } from 'lucide-react';
import { putAwayService, CreatePutAwayRequest, CreatePutAwayResponse, PutAwayReservationDto } from '../../../../services/putAwayService';
import { LocationSuggestionSource } from '../../../../types/putaway';
import { itemsService } from '../../../../services/itemsService';
import { ItemsDto } from '../../../../types/items';
import { StcTransDto } from '../../../../services/stcTransService';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { NumericInput } from '../../../common/inputs';
import { ExtendShelfModal } from '../../shared/ExtendShelfModal';

interface PutAwayModalProps {
  isOpen: boolean;
  onClose: () => void;
  stcTrans: StcTransDto;
  companyNo: string;
  warehouseID: string;
  onSuccess?: (reservations: PutAwayReservationDto[]) => void;
}

interface PutAwaySettings {
  binGrpNo: number;
  pickRuleNo: number;
  putAwayToPickableBinYesNo: boolean;
  quantity: number;
}

export const PutAwayModal: React.FC<PutAwayModalProps> = ({
  isOpen,
  onClose,
  stcTrans,
  companyNo,
  warehouseID,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadingItemData, setLoadingItemData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreatePutAwayResponse | null>(null);
  const [itemData, setItemData] = useState<ItemsDto | null>(null);
  const [showExtendShelfModal, setShowExtendShelfModal] = useState(false);
  const [lastCreatedBinCode, setLastCreatedBinCode] = useState<string | null>(null);
  const [settings, setSettings] = useState<PutAwaySettings>({
    binGrpNo: 1,
    pickRuleNo: 1,
    putAwayToPickableBinYesNo: true,
    quantity: stcTrans.quantity || 0
  });

  // Load item data when modal opens
  useEffect(() => {
    if (isOpen && stcTrans.itemNo) {
      loadItemData();
    }
  }, [isOpen, stcTrans.itemNo, companyNo]);

  const loadItemData = async () => {
    try {
      setLoadingItemData(true);
      setError(null);
      
      const item = await itemsService.getItemByCompanyAndItemNo(companyNo, stcTrans.itemNo);
      setItemData(item);
      
      // Update settings with item data
      setSettings(prev => ({
        ...prev,
        binGrpNo: item.binGrpNo || 1,
        pickRuleNo: item.pickRuleNo || 1,
        putAwayToPickableBinYesNo: item.putAwayToPickableBinYesNo ?? true
      }));
      
    } catch (err) {
      console.error('Error loading item data:', err);
      setError(t('warehouse.modules.receipt.putAway.errors.loadItemDataFailed', { 0: err instanceof Error ? err.message : t('common.errors.unknownError') }));
    } finally {
      setLoadingItemData(false);
    }
  };

  const handleRunPutAway = async () => {
    if (!stcTrans.itemNo || settings.quantity <= 0) {
      setError(t('warehouse.modules.receipt.putAway.errors.itemAndQuantityRequired'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResult(null);

      const request: CreatePutAwayRequest = {
        companyNo,
        warehouseID,
        itemNo: stcTrans.itemNo,
        binGrpNo: settings.binGrpNo,
        pickRuleNo: settings.pickRuleNo,
        grShipmentNo: stcTrans.grShipmentNo || 0,
        putAwayToPickableBinYesNo: settings.putAwayToPickableBinYesNo,
        quantity: settings.quantity,
        // For Standard mode - needed to lookup POrderLine.StdLocationName from ERP
        pOrderNo: stcTrans.orderNo || undefined,
        pOrderLineNo: stcTrans.lineNo || undefined
      };

      const response = await putAwayService.createPutAwayForStcIntRelOrderLine(request);
      setResult(response);

      // Don't call onSuccess automatically as it might close the modal
      // onSuccess will be called when user explicitly closes modal with results
      // if (response.success && onSuccess) {
      //   onSuccess(response.reservations);
      // }
    } catch (err) {
      console.error('Error running put-away:', err);
      setError(t('warehouse.modules.receipt.putAway.errors.runFailed', { 0: err instanceof Error ? err.message : t('common.errors.unknownError') }));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Call onSuccess callback if we have successful results
    if (result?.success && onSuccess) {
      onSuccess(result.reservations);
    }
    
    // Reset state
    setResult(null);
    setError(null);
    setItemData(null);
    setLoadingItemData(false);
    setSettings({
      binGrpNo: 1,
      pickRuleNo: 1,
      putAwayToPickableBinYesNo: true,
      quantity: stcTrans.quantity || 0
    });
    
    onClose();
  };

  const handleStartOver = () => {
    setResult(null);
    setError(null);
    setItemData(null);
    setLoadingItemData(false);
    setSettings({
      binGrpNo: 1,
      pickRuleNo: 1,
      putAwayToPickableBinYesNo: true,
      quantity: stcTrans.quantity || 0
    });
    
    // Load item data again to populate settings from item
    if (stcTrans.itemNo) {
      loadItemData();
    }
  };

  const getPickRuleText = (pickRuleNo: number) => {
    switch (pickRuleNo) {
      case 1: return t('warehouse.modules.receipt.putAway.pickRule.fifo');
      case 2: return t('warehouse.modules.receipt.putAway.pickRule.lifo');
      case 3: return t('warehouse.modules.receipt.putAway.pickRule.smallestFirst');
      case 4: return t('warehouse.modules.receipt.putAway.pickRule.largestFirst');
      default: return t('warehouse.modules.receipt.putAway.pickRule.ruleNumber', { 0: pickRuleNo });
    }
  };

  const getLocationSourceInfo = (source: LocationSuggestionSource, isStandardMode: boolean) => {
    switch (source) {
      case LocationSuggestionSource.ErpPOrderLine:
        return {
          text: t('warehouse.modules.receipt.putAway.locationSource.erpPurchaseOrder'),
          description: t('warehouse.modules.receipt.putAway.locationSource.erpPurchaseOrderDesc'),
          icon: '📦',
          color: 'blue'
        };
      case LocationSuggestionSource.ItemStdBinCode:
        return {
          text: t('warehouse.modules.receipt.putAway.locationSource.itemStdLocation'),
          description: t('warehouse.modules.receipt.putAway.locationSource.itemStdLocationDesc'),
          icon: '🏷️',
          color: 'green'
        };
      case LocationSuggestionSource.Calculated:
        return {
          text: t('warehouse.modules.receipt.putAway.locationSource.calculated'),
          description: t('warehouse.modules.receipt.putAway.locationSource.calculatedDesc'),
          icon: '⚙️',
          color: 'purple'
        };
      case LocationSuggestionSource.Placeholder:
        return {
          text: t('warehouse.modules.receipt.putAway.locationSource.placeholder'),
          description: t('warehouse.modules.receipt.putAway.locationSource.placeholderDesc'),
          icon: '⚠️',
          color: 'orange'
        };
      case LocationSuggestionSource.None:
      default:
        return {
          text: t('common.fields.notSpecified'),
          description: t('warehouse.modules.receipt.putAway.locationSource.noneDesc'),
          icon: '❓',
          color: 'gray'
        };
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-0 sm:p-4 z-50">
      <div className="bg-white w-full h-full sm:rounded-lg sm:shadow-xl sm:w-full sm:max-w-4xl sm:max-h-[90vh] overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
            <Package className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500 flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{t('warehouse.modules.receipt.putAway.title')}</h2>
              <p className="text-xs sm:text-sm text-gray-600 truncate">
                {t('common.fields.itemNumber')}: {stcTrans.itemNo} • {t('common.fields.quantity')}: {stcTrans.quantity} {stcTrans.itemUnitTxt}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* StcTrans Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('warehouse.modules.receipt.directReceipt.transactionInfo')}</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-600">{t('common.fields.itemNumber')}:</span>
                <div className="font-medium">{stcTrans.itemNo}</div>
              </div>
              <div>
                <span className="text-gray-600">{t('common.fields.description')}:</span>
                <div className="font-medium">{stcTrans.description1 || t('common.fields.notSpecified')}</div>
              </div>
              <div>
                <span className="text-gray-600">{t('common.fields.quantity')}:</span>
                <div className="font-medium">{stcTrans.quantity} {stcTrans.itemUnitTxt}</div>
              </div>
              <div>
                <span className="text-gray-600">{t('warehouse.general.location')}:</span>
                <div className="font-medium">{stcTrans.locationName || t('common.fields.notSpecified')}</div>
              </div>
              <div>
                <span className="text-gray-600">{t('warehouse.modules.receipt.putAway.grShipment')}:</span>
                <div className="font-medium">{stcTrans.grShipmentNo || t('common.fields.notSpecified')}</div>
              </div>
              <div>
                <span className="text-gray-600">{t('warehouse.modules.receipt.putAway.warehouse')}:</span>
                <div className="font-medium">{stcTrans.warehouse || warehouseID}</div>
              </div>
            </div>
          </div>

          {/* Item Data Loading */}
          {loadingItemData && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                <span className="text-blue-700">{t('warehouse.modules.receipt.putAway.loadingItemData')}</span>
              </div>
            </div>
          )}

          {/* Item Data Info */}
          {itemData && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-green-900 mb-3 flex items-center space-x-2">
                <CheckCircle className="h-4 w-4" />
                <span>{t('warehouse.modules.receipt.putAway.itemDataLoaded')}</span>
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-green-600">{t('warehouse.modules.receipt.putAway.standardBin')}:</span>
                  <div className="font-medium">{itemData.stdBinCode || t('common.fields.notSpecified')}</div>
                </div>
                <div>
                  <span className="text-green-600">{t('warehouse.modules.receipt.putAway.binGroup')}:</span>
                  <div className="font-medium">{itemData.binGrpNo || t('common.fields.notSpecified')}</div>
                </div>
                <div>
                  <span className="text-green-600">{t('warehouse.modules.receipt.putAway.pickRuleLabel')}:</span>
                  <div className="font-medium">{itemData.pickRuleNo || t('common.fields.notSpecified')}</div>
                </div>
                <div>
                  <span className="text-green-600">{t('warehouse.modules.receipt.putAway.pickableBin')}:</span>
                  <div className="font-medium">{itemData.putAwayToPickableBinYesNo ? t('warehouse.modules.receipt.putAway.yes') : t('warehouse.modules.receipt.putAway.no')}</div>
                </div>
                <div>
                  <span className="text-green-600">{t('warehouse.modules.receipt.putAway.itemType')}:</span>
                  <div className="font-medium">{itemData.itemType || t('warehouse.modules.receipt.putAway.standard')}</div>
                </div>
                <div>
                  <span className="text-green-600">{t('warehouse.modules.receipt.putAway.sharedBin')}:</span>
                  <div className="font-medium">{itemData.useSharedBinYesNo ? t('warehouse.modules.receipt.putAway.yes') : t('warehouse.modules.receipt.putAway.no')}</div>
                </div>
              </div>
            </div>
          )}

          {/* Put-Away Settings */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center space-x-2">
              <Settings className="h-4 w-4" />
              <span>{t('warehouse.modules.receipt.putAway.settingsTitle')}</span>
              {itemData && (
                <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                  {t('warehouse.modules.receipt.putAway.fromItemData')}
                </span>
              )}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('warehouse.modules.receipt.putAway.binGroupNo')}
                  {itemData && itemData.binGrpNo && (
                    <span className="text-xs text-green-600 ml-1">{t('warehouse.modules.receipt.putAway.fromItemDataInline')}</span>
                  )}
                </label>
                <NumericInput
                  mode="integer"
                  value={settings.binGrpNo}
                  onChange={(e) => setSettings(prev => ({ ...prev, binGrpNo: parseInt(e.target.value) || 1 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('warehouse.modules.receipt.putAway.pickRuleNo')}
                  {itemData && itemData.pickRuleNo && (
                    <span className="text-xs text-green-600 ml-1">{t('warehouse.modules.receipt.putAway.fromItemDataInline')}</span>
                  )}
                </label>
                <select
                  value={settings.pickRuleNo}
                  onChange={(e) => setSettings(prev => ({ ...prev, pickRuleNo: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={1}>1 - {t('warehouse.modules.receipt.putAway.pickRule.fifo')}</option>
                  <option value={2}>2 - {t('warehouse.modules.receipt.putAway.pickRule.lifo')}</option>
                  <option value={3}>3 - {t('warehouse.modules.receipt.putAway.pickRule.smallestFirst')}</option>
                  <option value={4}>4 - {t('warehouse.modules.receipt.putAway.pickRule.largestFirst')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('warehouse.modules.receipt.putAway.quantityForPutAway')}
                </label>
                <NumericInput
                  mode="decimal"
                  value={settings.quantity}
                  onChange={(e) => setSettings(prev => ({ ...prev, quantity: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                  max={stcTrans.quantity || 0}
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="putAwayToPickableBin"
                  checked={settings.putAwayToPickableBinYesNo}
                  onChange={(e) => setSettings(prev => ({ ...prev, putAwayToPickableBinYesNo: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="putAwayToPickableBin" className="text-sm text-gray-700">
                  {t('warehouse.modules.receipt.putAway.putAwayToPickableBin')}
                  {itemData && itemData.putAwayToPickableBinYesNo !== undefined && (
                    <span className="text-xs text-green-600 ml-1">{t('warehouse.modules.receipt.putAway.fromItemDataInline')}</span>
                  )}
                </label>
              </div>
            </div>
          </div>

          {/* Extend shelf — opprett ny lokasjon før regelmotoren kjøres.
              Ingen auto-velg: regelmotoren bestemmer plassering, men ny bin er nå
              tilgjengelig og kan bli plukket opp avhengig av BinGrp/regler. */}
          {!result && (
            <>
              <button
                type="button"
                onClick={() => setShowExtendShelfModal(true)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-dashed border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 hover:border-blue-400 transition-colors text-sm font-medium"
                title={t('warehouse.extendShelf.buttonTitle')}
              >
                <PlusSquare className="h-4 w-4" />
                <span>{t('warehouse.extendShelf.inlineButton')}</span>
              </button>
              {lastCreatedBinCode && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                  <span className="font-medium">{lastCreatedBinCode}</span>{' '}
                  {t('warehouse.extendShelf.createdAndAvailable')}
                </div>
              )}
            </>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <span className="text-red-700">{error}</span>
              </div>
            </div>
          )}

          {/* Results */}
          {result && (
            <div className="space-y-4">
              {result.success ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <CheckCircle className="h-6 w-6 text-green-500" />
                    <div>
                      <h3 className="text-lg font-semibold text-green-800">{t('warehouse.modules.receipt.putAway.putAwayDone')}</h3>
                      <p className="text-sm text-green-600">
                        {t('warehouse.modules.receipt.putAway.reservationsCreated', { 0: result.reservations.length, 1: settings.quantity, 2: stcTrans.itemUnitTxt })}
                      </p>
                    </div>
                  </div>

                  {/* Warehouse Mode and Location Source Info */}
                  <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className={`bg-white rounded-lg p-3 border ${result.isStandardMode ? 'border-blue-200' : 'border-purple-200'}`}>
                      <div className="text-sm font-medium mb-1">
                        {result.isStandardMode ? `📋 ${t('warehouse.modules.receipt.putAway.standardMode')}` : `⚙️ ${t('warehouse.modules.receipt.putAway.proMode')}`}
                      </div>
                      <div className="text-xs text-gray-600">
                        {result.isStandardMode
                          ? t('warehouse.modules.receipt.putAway.standardModeDesc')
                          : t('warehouse.modules.receipt.putAway.proModeDesc')}
                      </div>
                    </div>

                    {(() => {
                      const sourceInfo = getLocationSourceInfo(result.suggestedLocationSource, result.isStandardMode);
                      return (
                        <div className={`bg-white rounded-lg p-3 border border-${sourceInfo.color}-200`}>
                          <div className="text-sm font-medium mb-1">
                            {sourceInfo.icon} {sourceInfo.text}
                          </div>
                          <div className="text-xs text-gray-600">
                            {sourceInfo.description}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  
                  {/* Summary Statistics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <div className="text-sm text-green-600">{t('warehouse.modules.receipt.putAway.totalPlaced')}</div>
                      <div className="text-lg font-semibold text-green-800">
                        {result.reservations.reduce((sum, r) => sum + r.resQuantity, 0)} {stcTrans.itemUnitTxt}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <div className="text-sm text-green-600">{t('warehouse.modules.receipt.putAway.normalBins')}</div>
                      <div className="text-lg font-semibold text-green-800">
                        {result.reservations.filter(r => !r.dummy).length}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <div className="text-sm text-green-600">{t('warehouse.modules.receipt.putAway.pickableBins')}</div>
                      <div className="text-lg font-semibold text-green-800">
                        {result.reservations.filter(r => r.pickableBinYesNo && !r.dummy).length}
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <div className="text-sm text-green-600">{t('warehouse.modules.receipt.putAway.dummyReservations')}</div>
                      <div className="text-lg font-semibold text-orange-600">
                        {result.reservations.filter(r => r.dummy).length}
                      </div>
                    </div>
                  </div>
                  
                  {/* Reservations Table */}
                  <div className="bg-white rounded-lg border border-green-200 overflow-hidden">
                    <div className="px-4 py-3 bg-green-100 border-b border-green-200">
                      <h4 className="text-sm font-medium text-green-900 flex items-center space-x-2">
                        <MapPin className="h-4 w-4" />
                        <span>{t('warehouse.modules.receipt.putAway.binReservations')}</span>
                        <span className="text-xs bg-green-200 px-2 py-1 rounded">
                          {t('warehouse.modules.receipt.putAway.reservationCount', { 0: result.reservations.length })}
                        </span>
                      </h4>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-green-200">
                        <thead className="bg-green-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-green-700 uppercase tracking-wider">
                              {t('warehouse.modules.receipt.putAway.binCode')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-green-700 uppercase tracking-wider">
                              {t('warehouse.general.zone')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-green-700 uppercase tracking-wider">
                              {t('common.fields.quantity')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-green-700 uppercase tracking-wider">
                              {t('warehouse.modules.receipt.putAway.pickableHeader')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-green-700 uppercase tracking-wider">
                              {t('warehouse.modules.receipt.putAway.resource')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-green-700 uppercase tracking-wider">
                              {t('common.fields.status')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-green-100">
                          {result.reservations.map((reservation, index) => (
                            <tr key={index} className={reservation.dummy ? 'bg-orange-50' : 'hover:bg-green-50'}>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                <div className="flex items-center space-x-2">
                                  <MapPin className={`h-4 w-4 ${reservation.dummy ? 'text-orange-500' : 'text-green-500'}`} />
                                  <span className={reservation.dummy ? 'font-mono' : ''}>{reservation.binCode}</span>
                                  {reservation.dummy && (
                                    <span className="text-xs bg-orange-100 text-orange-600 px-1 py-0.5 rounded">
                                      {t('warehouse.modules.receipt.directReceipt.dummy')}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {reservation.stcZoneID || t('common.fields.notSpecified')}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {reservation.resQuantity} {stcTrans.itemUnitTxt}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {reservation.pickableBinYesNo ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    {t('warehouse.modules.receipt.putAway.yes')}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    {t('warehouse.modules.receipt.putAway.no')}
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                #{reservation.resourceNo}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {reservation.dummy ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    {t('warehouse.modules.receipt.putAway.manualPlacement')}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    {t('warehouse.modules.receipt.putAway.automatic')}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  
                  {/* Dummy Reservations Warning */}
                  {result.reservations.some(r => r.dummy) && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-center space-x-2">
                        <AlertCircle className="h-5 w-5 text-orange-500" />
                        <div>
                          <h4 className="text-sm font-medium text-orange-900">{t('warehouse.modules.receipt.putAway.manualPlacementNeeded')}</h4>
                          <p className="text-sm text-orange-700 mt-1">
                            {t('warehouse.modules.receipt.putAway.manualPlacementNeededDesc')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                    <div>
                      <h3 className="text-red-800 font-medium">{t('warehouse.modules.receipt.putAway.putAwayFailed')}</h3>
                      <p className="text-sm text-red-600 mt-2">
                        {result.errorMessage || t('warehouse.modules.receipt.putAway.unknownErrorDuringPutAway')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {loadingItemData ? (
              <div className="flex items-center space-x-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{t('warehouse.modules.receipt.putAway.loadingItemData')}</span>
              </div>
            ) : itemData ? (
              <span>{t('warehouse.modules.receipt.putAway.settingsFromItemData')}</span>
            ) : (
              <span>{t('warehouse.modules.receipt.putAway.usingDefaultSettings')}</span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {result?.success && (
              <button
                onClick={handleStartOver}
                className="px-4 py-2 text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
              >
                {t('warehouse.modules.receipt.putAway.runAgain')}
              </button>
            )}
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {result?.success ? t('common.buttons.close') : t('common.buttons.cancel')}
            </button>
            {!result?.success && (
              <button
                onClick={handleRunPutAway}
                disabled={loading || loadingItemData || settings.quantity <= 0}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{t('warehouse.modules.receipt.putAway.running')}</span>
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    <span>{t('warehouse.modules.receipt.putAway.runPutAway')}</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Extend Shelf Modal — operatør oppretter ny lokasjon før regelmotoren kjøres.
          Regelmotoren får så muligheten til å bruke den nye lokasjonen (avhengig av BinGrp/regler).
          Ingen auto-velg siden plassering er algoritmisk i denne modalen. */}
      <ExtendShelfModal
        isOpen={showExtendShelfModal}
        onClose={() => setShowExtendShelfModal(false)}
        initialParentBinCode=""
        onCreated={(newBinCode) => {
          setLastCreatedBinCode(newBinCode);
          setShowExtendShelfModal(false);
        }}
      />
    </div>
  );
};
